import { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import SignatureGate from 'components/signature/SignatureGate';
import * as EmployeeService from 'services/EmployeeService';
import * as InterventionReportService from 'services/InterventionReportService';
import { CreateInterventionReportRequest, Employee, EmployeeWithUserInfo, InterventionReportPrefillResponse } from 'utils/interfaces';
import { toHHmm } from 'utils/common-utils';
import InterventionReportForm from './components/InterventionReportForm';
import { generateReportPdf } from './pdf/generateReportPdf';
import { EMPTY_PRESTAZIONE } from './prestazioneConfig';
import { displayableClientEmail, displayReportDate, ReportFormState } from './types';

interface RouteParams {
  taskId: string;
}

export default function FillReportPage() {
  const { taskId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const basePath = location.pathname.startsWith('/admin') ? '/admin' : '/dipendente';
  const listPath = basePath === '/admin' ? '/admin/piano-settimanale' : '/dipendente/interventi';

  const [loading, setLoading] = useState(true);
  const [missingSignature, setMissingSignature] = useState(false);
  const [profile, setProfile] = useState<EmployeeWithUserInfo | null>(null);
  const [prefill, setPrefill] = useState<InterventionReportPrefillResponse | null>(null);
  const [employeeSignatureUrl, setEmployeeSignatureUrl] = useState<string | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const employeeId = profile?.id ?? null;
  const employeeName = profile ? `${profile.firstName} ${profile.lastName}` : '';

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    const onSessionExpired = () => keycloak?.logout();

    const profileData = await EmployeeService.getMyProfile(token, onSessionExpired);
    if (!profileData) {
      setLoading(false);
      return;
    }
    setProfile(profileData);

    if (!profileData.hasSignature) {
      setMissingSignature(true);
      setLoading(false);
      return;
    }
    setMissingSignature(false);

    const [prefillData, employeesData, signatureBlob] = await Promise.all([
      InterventionReportService.getPrefill(token, onSessionExpired, Number(taskId)),
      EmployeeService.getAllPaginated(token, onSessionExpired, 0, 100),
      EmployeeService.downloadSignatureFile(token, onSessionExpired, profileData.id),
    ]);

    if (prefillData) setPrefill(prefillData);
    if (employeesData) setAllEmployees(employeesData.content);
    if (signatureBlob) setEmployeeSignatureUrl(URL.createObjectURL(signatureBlob));

    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, taskId]);

  const handleSubmit = async (formData: ReportFormState, customerSignatureDataUrl: string | null) => {
    if (!token || !prefill || !employeeId) return;
    setIsSubmitting(true);

    const payload: CreateInterventionReportRequest = {
      taskId: Number(taskId),
      clientName: formData.clientName,
      clientCity: formData.clientCity,
      clientAddress: formData.clientAddress,
      clientTaxCode: formData.clientTaxCode || undefined,
      clientVatNumber: formData.clientVatNumber || undefined,
      clientPhone: formData.clientPhone || undefined,
      clientEmail: formData.clientEmail || undefined,
      prestazione: formData.prestazione,
      notes: formData.notes || undefined,
      startTime: formData.startTime || undefined,
      endTime: formData.endTime || undefined,
      operators: formData.operators.map(o => ({ employeeId: o.employeeId, present: o.present })),
      attendees: formData.attendees,
      vehicleIds: formData.vehicles.filter(v => v.selected).map(v => v.id),
      customerSignerName: formData.customerSignerName || undefined,
      customerSignatureDataUrl: customerSignatureDataUrl || undefined,
      serviceRating: formData.serviceRating,
    };

    try {
      const pdfBlob = await generateReportPdf({
        reportNumberFormatted: prefill.suggestedReportNumberFormatted,
        reportDate: displayReportDate(prefill.reportDate),
        taskTitle: prefill.taskTitle,
        compiledByLabel: employeeName,
        employeeSignatureUrl,
        data: { ...formData, customerSignatureDataUrl },
      });

      const newAttachmentFiles = formData.attachments.map(a => a.file).filter((f): f is File => Boolean(f));
      const result = await InterventionReportService.createReport(token, () => keycloak?.logout(), payload, pdfBlob, newAttachmentFiles);

      switch (result.status) {
        case 'success':
          toast.success('Rapporto d\'intervento inviato con successo');
          history.push(listPath);
          window.scrollTo(0, 0);
          break;
        case 'missing_signature':
          toast.error('Devi configurare la tua firma prima di poter inviare il rapporto');
          break;
        case 'conflict':
          if (result.existingReportId) {
            toast.error('Esiste già un rapporto per questo intervento');
            history.push(`${basePath}/rapporti-intervento/${result.existingReportId}`);
          } else {
            toast.error('Impossibile salvare: il task non risulta completato o esiste già un rapporto');
          }
          break;
        case 'forbidden':
          toast.error('Non sei tra gli operatori assegnati a questo intervento');
          break;
        default:
          toast.error('Errore durante l\'invio del rapporto');
      }
    } catch (err) {
      toast.error('Errore durante la generazione del PDF');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '60px', md: '80px', xl: '78px' }}>
        <Center minH="60vh"><Spinner size="lg" /></Center>
      </Box>
    );
  }

  if (missingSignature && profile) {
    return (
      <Box>
        <Center pt={{ base: '16px', md: '24px' }} px={{ base: '16px', md: 0 }}>
          <Text fontSize="md" fontWeight="bold" color="red.500" textAlign="center">
            Devi configurare la tua firma prima di poter compilare un rapporto d'intervento.
          </Text>
        </Center>
        <SignatureGate
          employeeId={profile.id}
          employeeName={`${profile.firstName} ${profile.lastName}`}
          onSignatureComplete={() => loadData()}
        />
      </Box>
    );
  }

  if (!prefill || !employeeId) {
    return (
      <Box pt={{ base: '60px', md: '80px', xl: '78px' }}>
        <Center minH="60vh"><Text color="red.500">Impossibile caricare i dati dell'intervento.</Text></Center>
      </Box>
    );
  }

  const initialData: ReportFormState = {
    reportDate: prefill.reportDate,
    clientName: prefill.clientName || '',
    clientCity: prefill.clientCity || '',
    clientAddress: prefill.clientAddress || '',
    clientTaxCode: prefill.clientTaxCode || '',
    clientVatNumber: prefill.clientVatNumber || '',
    clientPhone: prefill.clientPhone || '',
    clientEmail: displayableClientEmail(prefill.clientEmail),
    prestazione: EMPTY_PRESTAZIONE,
    notes: prefill.taskType,
    startTime: toHHmm(prefill.startTime),
    endTime: toHHmm(prefill.endTime),
    operators: prefill.operators.map(o => ({ employeeId: o.employeeId, firstName: o.firstName, lastName: o.lastName, present: true })),
    attendees: [],
    vehicles: prefill.vehicles.map(v => ({ id: v.id, name: v.name, plate: v.plate, selected: true })),
    customerSignerName: '',
    customerSignatureDataUrl: null,
    serviceRating: null,
    attachments: [],
  };

  return (
    <Box pt={{ base: '40px', md: '80px', xl: '78px' }} pb="40px" px={{ base: 2, md: 4 }} mx={{ base: '-20px', md: 0 }}>
      <Text fontSize="2xl" fontWeight="bold" color="#9FB50C" mb="20px">
        Compila Rapporto d'Intervento
      </Text>
      <InterventionReportForm
        reportNumberFormatted={prefill.suggestedReportNumberFormatted}
        reportDate={displayReportDate(prefill.reportDate)}
        taskTitle={prefill.taskTitle}
        compiledByLabel={employeeName}
        employeeSignatureUrl={employeeSignatureUrl}
        allEmployees={allEmployees}
        initialData={initialData}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Invia rapporto"
        onCancel={() => history.push(listPath)}
      />
    </Box>
  );
}
